'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'About Us', href: '/about' },
  { label: 'Skill Development', href: '/skills' },
  { label: 'Register', href: '/registration-page' },
  { label: 'Verify Documents', href: '/document-verification-page' },
  { label: 'Contact', href: '/contact' },
];

export default function Header() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white shadow-elevated border-b border-border'
            : 'bg-white/95 backdrop-blur-sm border-b border-border/50'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2 flex-shrink-0">
              <AppLogo size={40} />
              <div className="flex flex-col">
                <span className="font-display text-lg font-800 text-primary leading-tight tracking-tight">
                  FCEP
                </span>
                <span className="text-xs text-muted-foreground font-medium leading-tight hidden sm:block">
                  Creator & Earning Program
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
              {navLinks?.map((link) => (
                <Link
                  key={`nav-${link?.href}`}
                  href={link?.href}
                  className={`nav-link ${pathname === link?.href ? 'active' : ''}`}
                >
                  {link?.label}
                </Link>
              ))}
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <Link
                href="/document-verification-page"
                className="btn-secondary text-sm px-4 py-2"
                style={{ padding: '0.5rem 1.25rem' }}
              >
                Login
              </Link>
              <Link
                href="/registration-page"
                className="btn-primary text-sm"
                style={{ padding: '0.5rem 1.25rem' }}
              >
                <Icon name="UserPlusIcon" size={16} variant="outline" />
                Register Free
              </Link>
            </div>

            {/* Mobile Hamburger */}
            <button
              className="lg:hidden p-2 rounded-md text-foreground hover:bg-muted transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            >
              <Icon
                name={mobileOpen ? 'XMarkIcon' : 'Bars3Icon'}
                size={24}
                variant="outline"
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-72 bg-white z-50 shadow-hero transform transition-transform duration-300 lg:hidden ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <AppLogo size={32} />
            <span className="font-display text-base font-700 text-primary">FCEP</span>
          </div>
          <button
            className="p-2 rounded-md hover:bg-muted transition-colors"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            <Icon name="XMarkIcon" size={20} variant="outline" />
          </button>
        </div>

        <nav className="p-4 flex flex-col gap-1">
          {navLinks?.map((link) => (
            <Link
              key={`mobile-nav-${link?.href}`}
              href={link?.href}
              className={`flex items-center px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                pathname === link?.href
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground hover:bg-muted'
              }`}
            >
              {link?.label}
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-border flex flex-col gap-3">
          <Link
            href="/document-verification-page"
            className="btn-secondary w-full justify-center text-sm"
            style={{ padding: '0.625rem 1rem' }}
          >
            Login
          </Link>
          <Link
            href="/registration-page"
            className="btn-primary w-full justify-center text-sm"
            style={{ padding: '0.625rem 1rem' }}
          >
            <Icon name="UserPlusIcon" size={16} variant="outline" />
            Register Free
          </Link>
        </div>

        <div className="p-4 mx-4 rounded-lg bg-muted">
          <p className="text-xs text-muted-foreground font-medium">
            📞 9007493290
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            ✉️ www.facebook.earning@gmail.com
          </p>
        </div>
      </div>

      {/* Spacer for fixed header */}
      <div className="h-16 lg:h-20" />
    </>
  );
}